import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import React from 'react';

import { TUseVirtualListProps } from './types';

const SCROLL_LOAD_THRESHOLD_PX = 80;

type TSharedVirtualListProps = TUseVirtualListProps & {
  enabled: boolean;
};

const isNearBottom = (container: HTMLDivElement) => {
  const { scrollHeight, scrollTop, clientHeight } = container;
  return scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX;
};

const isWindowNearBottom = () => {
  if (typeof window === 'undefined') return false;

  const { scrollY, innerHeight } = window;
  const { scrollHeight } = document.documentElement;

  return scrollHeight - scrollY - innerHeight < SCROLL_LOAD_THRESHOLD_PX;
};

function useContainerVirtualList({
  count,
  estimateSize,
  onBottomReached,
  overscan = 5,
  enabled,
}: TSharedVirtualListProps) {
  const rootRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: enabled ? count : 0,
    estimateSize,
    overscan,
    getScrollElement: () => (enabled ? rootRef.current : null),
    measureElement: (element) => element?.getBoundingClientRect().height ?? 0,
  });

  const onScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!enabled) return;

      if (isNearBottom(e.currentTarget)) {
        onBottomReached?.();
      }
    },
    [enabled, onBottomReached]
  );

  const scrollToTop = React.useCallback(() => {
    rootRef.current?.scrollTo({ top: 0 });
    rowVirtualizer.scrollToIndex(0);
  }, [rowVirtualizer]);

  return {
    rootRef,
    rowVirtualizer,
    onScroll,
    scrollToTop,
    scrollMode: 'container' as const,
  };
}

function usePageVirtualList({
  count,
  estimateSize,
  onBottomReached,
  overscan = 5,
  enabled,
}: TSharedVirtualListProps) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  const updateScrollMargin = React.useCallback(() => {
    if (!enabled || !rootRef.current) return;

    setScrollMargin(rootRef.current.offsetTop);
  }, [enabled]);

  React.useLayoutEffect(() => {
    if (!enabled) return;

    updateScrollMargin();
    window.addEventListener('resize', updateScrollMargin);

    return () => window.removeEventListener('resize', updateScrollMargin);
  }, [enabled, updateScrollMargin, count]);

  const rowVirtualizer = useWindowVirtualizer({
    count: enabled ? count : 0,
    estimateSize,
    overscan,
    scrollMargin,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 0,
  });

  React.useLayoutEffect(() => {
    if (!enabled) return;

    rowVirtualizer.measure();
  }, [enabled, count, rowVirtualizer]);

  React.useEffect(() => {
    if (!enabled) return;

    const handleWindowScroll = () => {
      if (isWindowNearBottom()) {
        onBottomReached?.();
      }
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [enabled, onBottomReached]);

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: scrollMargin, behavior: 'instant' });
    rowVirtualizer.scrollToIndex(0);
  }, [rowVirtualizer, scrollMargin]);

  return {
    rootRef,
    rowVirtualizer,
    onScroll: undefined,
    scrollToTop,
    scrollMode: 'page' as const,
  };
}

export function useVirtualList({
  scrollMode = 'container',
  ...props
}: TUseVirtualListProps) {
  const isPageMode = scrollMode === 'page';
  const container = useContainerVirtualList({ ...props, scrollMode, enabled: !isPageMode });
  const page = usePageVirtualList({ ...props, scrollMode, enabled: isPageMode });

  return isPageMode ? page : container;
}
